import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, Download, Upload, FileText, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnrollmentPortalPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portalData, setPortalData] = useState(null);
  const [uploadingType, setUploadingType] = useState(null);
  const fileInputRefs = useRef({});

  const DOCUMENT_TYPES = [
    { key: "shot_records",         label: "Child Shot Records" },
    { key: "enrollment_info",      label: "Enrollment Information Sheet" },
    { key: "financial_contract",   label: "Financial & Contract Agreement" },
    { key: "about_child",          label: "All About Your Child Form" },
    { key: "health_report",        label: "Child Health Report" },
    { key: "emergency_consent",    label: "Emergency Contact / Parental Consent Form" },
    { key: "transportation_policy",label: "School Age Transportation & Transfer of Care Policy" },
  ];

  useEffect(() => {
    const fetchPortal = async () => {
      try {
        const res = await axios.get(`${API}/enrollment-packet/portal/${token}`);
        setPortalData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "This enrollment link is invalid or has expired.");
      } finally {
        setLoading(false);
      }
    };
    fetchPortal();
  }, [token]);

  const handleUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingType(docType);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(
        `${API}/enrollment-packet/portal/${token}/upload?document_type=${docType}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setPortalData(prev => ({
        ...prev,
        uploadedDocTypes: res.data.uploadedDocTypes,
        pendingReplacementDocTypes: res.data.pendingReview
          ? [...new Set([...(prev.pendingReplacementDocTypes || []), docType])]
          : (prev.pendingReplacementDocTypes || []).filter(t => t !== docType),
        customFormStatus: res.data.customFormStatus || prev.customFormStatus || {},
      }));
      toast.success(res.data.pendingReview
        ? "File uploaded successfully and sent to the center for review."
        : "Form uploaded successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploadingType(null);
      if (fileInputRefs.current[docType]) fileInputRefs.current[docType].value = "";
    }
  };

  const handleAcknowledge = async (customId) => {
    try {
      const res = await axios.post(
        `${API}/enrollment-packet/portal/${token}/acknowledge?custom_id=${customId}`
      );
      setPortalData(prev => ({ ...prev, customFormStatus: res.data.customFormStatus }));
      toast.success("Marked as read!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mark as read. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Link Not Found</h2>
          <p className="text-gray-500">{error}</p>
          <p className="text-sm text-gray-400 mt-4">Please contact your childcare center for a new link.</p>
        </div>
      </div>
    );
  }

  const { childName, centerName, uploadedDocTypes = [], pendingReplacementDocTypes = [], templates = [], customTemplates = [], customFormStatus = {} } = portalData;
  const visibleDocTypes = DOCUMENT_TYPES.filter(({ key }) =>
    templates.some(t => t.documentType === key) || uploadedDocTypes.includes(key)
  );
  const uploadedCount = visibleDocTypes.filter(d => uploadedDocTypes.includes(d.key)).length;
  const totalCount = visibleDocTypes.length;
  const allComplete = totalCount > 0 && uploadedCount === totalCount;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 p-4 py-8">
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-cyan-500 rounded-2xl p-6 text-white text-center shadow-lg">
          <h1 className="text-2xl font-bold">{centerName}</h1>
          <p className="text-teal-100 mt-1">Enrollment Packet</p>
          <div className="mt-3 bg-white/20 rounded-lg px-4 py-2 inline-block">
            <span className="font-semibold">{childName}</span>
          </div>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-xl shadow-sm border border-teal-100 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Forms Received</span>
            <span className={`text-sm font-bold ${allComplete ? 'text-green-600' : 'text-teal-600'}`}>
              {uploadedCount} of {totalCount}
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-500 ${allComplete ? 'bg-green-500' : 'bg-teal-500'}`}
              style={{ width: `${(uploadedCount / totalCount) * 100}%` }}
            />
          </div>
          {allComplete && (
            <p className="text-green-600 text-sm font-medium mt-2 flex items-center gap-1">
              <CheckCircle className="w-4 h-4" /> All forms received — thank you!
            </p>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
          <strong>Instructions:</strong> For each form below — download the blank copy, print and complete it by hand, then upload the completed form using the Upload button.
        </div>

        {/* Wrong upload note */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          <strong>Uploaded the wrong form?</strong> Contact the center to have it removed, then refresh this page — the Upload button will reappear so you can submit the correct form.
        </div>

        {/* Form List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="divide-y divide-gray-100">
            {visibleDocTypes.map(({ key, label }) => {
              const template = templates.find(t => t.documentType === key);
              const isUploaded = uploadedDocTypes.includes(key);
              const isPendingReview = pendingReplacementDocTypes.includes(key);
              const isUploading = uploadingType === key;

              return (
                <div key={key} className={`flex items-center justify-between px-5 py-4 ${isUploaded ? (isPendingReview ? 'bg-amber-50' : 'bg-green-50') : ''}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {isUploaded
                      ? <CheckCircle className={`w-5 h-5 flex-shrink-0 ${isPendingReview ? 'text-amber-500' : 'text-green-500'}`} />
                      : <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />}
                    <div className="min-w-0">
                      <p className={`text-sm font-medium truncate ${isUploaded ? (isPendingReview ? 'text-amber-700' : 'text-green-700') : 'text-gray-800'}`}>{label}</p>
                      {isUploaded && (
                        <p className={`text-xs ${isPendingReview ? 'text-amber-600' : 'text-green-500'}`} data-testid={`form-status-${key}`}>
                          {isPendingReview ? 'Awaiting Center Review' : 'Received'}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                    {template && (
                      <Button size="sm" variant="outline"
                        className="h-8 text-xs gap-1 border-teal-300 text-teal-700 hover:bg-teal-50"
                        onClick={() => window.open(`${API}/enrollment-packet/portal/${token}/template/${key}`, '_blank')}
                        data-testid={`download-template-${key}`}>
                        <Download className="w-3.5 h-3.5" /> Download
                      </Button>
                    )}

                    {!isUploaded && (
                      <>
                        <input
                          type="file"
                          ref={el => fileInputRefs.current[key] = el}
                          className="hidden"
                          accept="application/pdf,image/jpeg,image/png,image/gif"
                          onChange={e => handleUpload(e, key)}
                          data-testid={`upload-input-${key}`}
                        />
                        <Button size="sm"
                          className="h-8 text-xs gap-1 bg-teal-600 hover:bg-teal-700 text-white"
                          onClick={() => fileInputRefs.current[key]?.click()}
                          disabled={isUploading}
                          data-testid={`upload-btn-${key}`}>
                          {isUploading ? (
                            <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <><Upload className="w-3.5 h-3.5" /> Upload</>
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Additional Center Forms */}
        {customTemplates.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-700">Additional Forms from {centerName}</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                These forms are provided by your center and do not count toward your state compliance score.
              </p>
            </div>
            <div className="divide-y divide-gray-100">
              {customTemplates.map(tmpl => {
                const status = customFormStatus[tmpl.customId];
                const isDone = !!status;
                const isUploading = uploadingType === `custom_${tmpl.customId}`;
                return (
                  <div key={tmpl.customId} className={`flex items-center justify-between px-5 py-4 ${isDone ? 'bg-green-50' : ''}`}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {isDone
                        ? <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        : <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />}
                      <div className="min-w-0">
                        <p className={`text-sm font-medium truncate ${isDone ? 'text-green-700' : 'text-gray-800'}`}>
                          {tmpl.customLabel}
                        </p>
                        {isDone && (
                          <p className="text-xs text-green-500">
                            {status === 'acknowledged' ? 'Marked as read' : 'Received'}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      {tmpl.storagePath && (
                        <Button size="sm" variant="outline"
                          className="h-8 text-xs gap-1 border-teal-300 text-teal-700 hover:bg-teal-50"
                          onClick={() => window.open(tmpl.storagePath, '_blank')}>
                          <Download className="w-3.5 h-3.5" /> Download
                        </Button>
                      )}
                      {!isDone && tmpl.returnRequired && (
                        <>
                          <input
                            type="file"
                            ref={el => fileInputRefs.current[`custom_${tmpl.customId}`] = el}
                            className="hidden"
                            accept="application/pdf,image/jpeg,image/png,image/gif"
                            onChange={e => handleUpload(e, `custom_${tmpl.customId}`)}
                          />
                          <Button size="sm"
                            className="h-8 text-xs gap-1 bg-teal-600 hover:bg-teal-700 text-white"
                            onClick={() => fileInputRefs.current[`custom_${tmpl.customId}`]?.click()}
                            disabled={isUploading}>
                            {isUploading
                              ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              : <><Upload className="w-3.5 h-3.5" /> Upload</>}
                          </Button>
                        </>
                      )}
                      {!isDone && !tmpl.returnRequired && (
                        <Button size="sm"
                          className="h-8 text-xs gap-1 bg-amber-500 hover:bg-amber-600 text-white"
                          onClick={() => handleAcknowledge(tmpl.customId)}>
                          <CheckCircle className="w-3.5 h-3.5" /> Mark as Read
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <p className="text-center text-xs text-gray-400 pb-4">
          This is a secure private link for {childName}'s enrollment. Please do not share it with others.
        </p>
      </div>
    </div>
  );
}
